/**
 * InactiveAccountSplash — Phase 3 §3.
 *
 * Renders when `user.status !== "active"` for a user trying to reach
 * a protected route. The splash REPLACES the role shell (no admin
 * sidebar / customer top-nav / provider sidebar around it) so the
 * user can't poke at workspace affordances while suspended.
 *
 * Log out works because `useAuth().logout()` is a pure frontend op
 * (clears localStorage, sets user=null) — no API call required, so
 * a suspended user can always sign out cleanly.
 *
 * Lives outside any chrome — public pages are still reachable so
 * the user can read `/contact` for support details after logging
 * back out.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShieldOff, LogOut } from "lucide-react";

export default function InactiveAccountSplash() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12 bg-pp-v3-bg"
      data-testid="inactive-account-splash"
    >
      <div
        className="w-full max-w-md rounded-3xl border border-pp-border bg-pp-surface p-10 text-center"
        style={{ boxShadow: "var(--pp-card-shadow)" }}
      >
        <ShieldOff
          className="mx-auto h-12 w-12 text-pp-orange"
          aria-hidden="true"
          data-testid="inactive-account-icon"
        />
        <h1
          className="mt-5 font-heading text-3xl font-bold text-pp-text"
          data-testid="inactive-account-headline"
        >
          Your account is inactive
        </h1>
        <p
          className="mt-3 text-sm text-pp-text-muted"
          data-testid="inactive-account-body"
        >
          Contact support to reactivate.
        </p>
        {user?.email && (
          <p
            className="mt-1 text-xs text-pp-text-soft"
            data-testid="inactive-account-email"
          >
            Signed in as {user.email}
          </p>
        )}
        <Button
          type="button"
          onClick={handleLogout}
          className="mt-6 h-12 w-full rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
          data-testid="inactive-account-logout-button"
        >
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
          Log out
        </Button>
      </div>
    </div>
  );
}
